<script setup lang="ts">
import { computed, onUnmounted, ref, watch, type ComponentPublicInstance } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import {
  autoUpdate,
  flip,
  limitShift,
  type Middleware,
  offset as floatingOffset,
  shift,
  size,
  useFloating,
} from '@floating-ui/vue';
import type { Placement } from '@/modules/common/types/placement.ts';
import { injectInModal } from '@/providers/in-modal.ts';
import {
  isInsideChildDropdownLayer,
  startDropdownLayer,
  startEscapeCapture,
  stopDropdownLayer,
  stopEscapeCapture,
} from '@/modules/ui/composables/use-dropdown-escape.ts';

export interface UiDropdownSlotProps {
  toggle: () => void;
  show: () => void;
  hide: () => void;
  isOpen: boolean;
}

const RESIZE_DURATION = 300;

const isOpen = defineModel<boolean>({ default: false });

const {
  tag = 'div',
  placement = ['bottom-start'],
  offset,
  fullWidth,
  shrink,
  shrinkPadding = 6,
  anchorWidth,
  fitContentWidth,
  closeIfInnerClick,
  closeIfOutsideClick,
  interactiveAnchor,
  animateResize,
  pinToAnchor,
  backdropColor = 'transparent',
} = defineProps<{
  tag?: 'div' | 'ul';
  placement?: Placement | Placement[];
  offset?: number | { mainAxis?: number; crossAxis?: number; alignmentAxis?: number };
  fullWidth?: boolean;
  shrink?: boolean;
  shrinkPadding?: number;
  anchorWidth?: boolean;
  fitContentWidth?: boolean;
  closeIfInnerClick?: boolean;
  closeIfOutsideClick?: boolean;
  interactiveAnchor?: boolean;
  animateResize?: boolean;
  pinToAnchor?: boolean;
  backdropColor?: 'gray' | 'transparent';
  noOverflow?: boolean;
}>();

const emit = defineEmits<{
  open: [];
  close: [];
}>();

defineSlots<{
  default(slotProps: UiDropdownSlotProps): unknown;
  anchor(slotProps: UiDropdownSlotProps): unknown;
}>();

const inModal = injectInModal();

const toggle = () => (isOpen.value = !isOpen.value);

const show = () => {
  if (!isOpen.value) {
    toggle();
  }
};

const hide = () => {
  if (isOpen.value) {
    toggle();
  }
};

function containsNode(refValue: HTMLElement | ComponentPublicInstance | null, node: Node) {
  if (!refValue) {
    return false;
  }

  const root = refValue instanceof Element ? refValue : refValue.$el;

  return root instanceof Element && root.contains(node);
}

function isInsideDropdown(node: Node) {
  return containsNode(anchorRef.value, node) || containsNode(dropdownRef.value, node);
}

const onDropdownClick = () => {
  if (closeIfInnerClick) {
    hide();
  }
};

function onDocumentPointerDown(event: PointerEvent) {
  if (!isOpen.value || !(event.target instanceof Node)) {
    return;
  }

  if (isInsideDropdown(event.target) || isInsideChildDropdownLayer(hide, event.target)) {
    return;
  }

  hide();
}

function onWindowDragEnter(event: DragEvent) {
  if (event.dataTransfer?.types.includes('Files')) {
    hide();
  }
}

const rootRef = ref(null);
const anchorRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | ComponentPublicInstance | null>(null);

const placementInitial = computed(() => (Array.isArray(placement) ? placement[0] : placement));
const fallbackPlacements = computed(() =>
  Array.isArray(placement) && placement.length > 1 ? placement.slice(1) : undefined
);

const middleware = computed(() => {
  return [
    floatingOffset(offset),
    shift({ mainAxis: !pinToAnchor, limiter: limitShift() }),
    flip({
      fallbackPlacements: fallbackPlacements.value,
    }),
    (shrink || anchorWidth || fitContentWidth) &&
      size({
        apply({ rects, availableWidth, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${anchorWidth ? rects.reference.width : availableWidth}px`,
            minWidth: fitContentWidth ? `${Math.min(rects.reference.width, availableWidth)}px` : '',
            maxHeight: shrink ? `${Math.max(availableHeight - shrinkPadding, 0)}px` : 'none',
          });
        },
      }),
  ].filter(Boolean) as Middleware[];
});

const { floatingStyles, update } = useFloating(anchorRef, dropdownRef, {
  placement: placementInitial.value,
  whileElementsMounted: (reference, floating, update) =>
    autoUpdate(reference, floating, update, { animationFrame: animateResize }),
  middleware,
});

function listenVisualViewport(listen: boolean) {
  const viewport = window.visualViewport;
  const method = listen ? 'addEventListener' : 'removeEventListener';

  viewport?.[method]('resize', update);
  viewport?.[method]('scroll', update);
}

let resizeAnimation: Animation | null = null;
let lastHeight: number | null = null;

function getDropdownElement() {
  const dropdown = dropdownRef.value;

  if (!dropdown) {
    return null;
  }

  const element = dropdown instanceof Element ? dropdown : dropdown.$el;

  return element instanceof HTMLElement ? element : null;
}

function stopResizeAnimation() {
  const element = getDropdownElement();

  if (element) {
    element.style.height = '';
  }

  resizeAnimation?.cancel();
  resizeAnimation = null;
  lastHeight = null;
}

useResizeObserver(dropdownRef, () => {
  const element = getDropdownElement();

  if (!animateResize || !element || resizeAnimation) {
    return;
  }

  const height = element.offsetHeight;

  if (lastHeight !== null && lastHeight !== height) {
    element.style.height = `${lastHeight}px`;

    resizeAnimation = element.animate([{ height: `${lastHeight}px` }, { height: `${height}px` }], {
      duration: RESIZE_DURATION,
      easing: 'ease-in-out',
      fill: 'both',
    });

    resizeAnimation.addEventListener('finish', () => {
      element.style.height = '';
      resizeAnimation?.cancel();
      resizeAnimation = null;
      lastHeight = getDropdownElement()?.offsetHeight ?? null;
    });
  }

  lastHeight = height;
});

watch(isOpen, (open) => {
  listenVisualViewport(open);

  if (open) {
    emit('open');
    startEscapeCapture(hide);
    startDropdownLayer(hide, isInsideDropdown);
    window.addEventListener('dragenter', onWindowDragEnter);

    if (closeIfOutsideClick || interactiveAnchor) {
      document.addEventListener('pointerdown', onDocumentPointerDown);
    }

    return;
  }

  emit('close');
  stopResizeAnimation();
  stopEscapeCapture(hide);
  stopDropdownLayer(hide);
  window.removeEventListener('dragenter', onWindowDragEnter);
  document.removeEventListener('pointerdown', onDocumentPointerDown);
});

onUnmounted(() => {
  listenVisualViewport(false);
  stopResizeAnimation();
  window.removeEventListener('dragenter', onWindowDragEnter);
  document.removeEventListener('pointerdown', onDocumentPointerDown);
  stopDropdownLayer(hide);
  stopEscapeCapture(hide);
});

defineExpose({ toggle, show, hide });
</script>

<template>
  <div
    ref="rootRef"
    class="ui-dropdown"
  >
    <div ref="anchorRef">
      <slot
        name="anchor"
        :toggle="toggle"
        :show="show"
        :hide="hide"
        :is-open="isOpen"
      />
    </div>

    <Teleport
      v-if="isOpen"
      to="body"
    >
      <div
        class="backdrop"
        :class="[{ '_in-modal': inModal, '_interactive': interactiveAnchor }, `_color-${backdropColor}`]"
      >
        <Transition name="opacity-fast">
          <component
            :is="tag"
            v-if="isOpen"
            ref="dropdownRef"
            class="dropdown"
            :class="{
              '_flex': shrink,
              '_full-width': fullWidth,
              '_fit-content-width': fitContentWidth,
              '_no-overflow': noOverflow,
            }"
            :style="floatingStyles"
            @click="onDropdownClick"
          >
            <slot
              :toggle="toggle"
              :show="show"
              :hide="hide"
              :is-open="isOpen"
            />
          </component>
        </Transition>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.backdrop {
  position: fixed;
  z-index: var(--z-index-dropdown);
  inset: 0;
  user-select: none;
  pointer-events: none;

  &._in-modal {
    z-index: var(--z-index-modal-dropdown);
  }

  &._interactive {
    .dropdown {
      pointer-events: auto;
    }
  }

  &._color-gray {
    background-color: var(--color-backdrop);
    backdrop-filter: blur(5px);
    pointer-events: auto;
  }
}

.dropdown {
  pointer-events: auto;
  background-color: var(--color-white);
  border-radius: 8px;
  box-shadow: var(--shadow);

  &:not(._no-overflow) {
    overflow: hidden;
  }

  &._flex {
    display: flex;
    flex-direction: column;
  }

  &._full-width {
    width: 100%;
  }

  &._fit-content-width {
    width: max-content;
  }
}
</style>
